import xmlFormat from 'xml-formatter';
import {
  Service,
  Divert,
  CheckboxConfigType,
  Template,
  XmlFilesContent,
  NON_CFS,
  BasicData,
  NameContent,
  AcceptorBasicData,
  CFS,
  TRUSTED_CFS,
  Address,
  ADAPTER,
  BIGDATA_INTEGRATION,
  AuditConfig,
} from 'models/husqiotbus';
import { requestResponse } from './defaultConfigService';
import { sortDiverts } from 'utils/husqiotbus';
import { generateBasicDataXml } from './generateBasicDataService';

const parser = new DOMParser();

function setExclusiveFalseForAddress(divertsXml: string, addressToMatch: string): string {
  const doc = parser.parseFromString(divertsXml, 'text/xml');

  for (const divertElement of Array.from(doc.querySelectorAll('divert'))) {
    const address = (divertElement.querySelector('address')?.textContent || '');
    if (address !== addressToMatch) continue;

    const exclusiveElement = divertElement.querySelector('exclusive');

    if (exclusiveElement && exclusiveElement.textContent === 'true') {
      exclusiveElement.textContent = 'false';
    }
  }

  return new XMLSerializer().serializeToString(doc);
}
type DivertAuditConfig = {
  name: string;
  address: string;
  filter: string;
};

const SYSTEM_XML_TEMPLATE =
  '<system ordinality="optional">{systemUsers}</system>';
const CFS_AUTHORIZER_TEMPLATE =
  '<authorize><pre-authorized-authorizer><pre-authorized-prefix>iot.{cfs}</pre-authorized-prefix>{system}</pre-authorized-authorizer></authorize>';
const CFS_AUTHORIZER_ROLE_TEMPLATE =
  '<authorize><role-pre-authorized-authorizer><pre-authorized-prefix>iot.{cfs}</pre-authorized-prefix>{system}</role-pre-authorized-authorizer></authorize>';
const ANNOTATIONS_TEMPLATE = '<annotations></annotations>';
const ADDRESS_TEMPLATE =
  ' <address name="iot.{serviceName}.{address}"><anycast><queue name="iot.{serviceName}.{address}" {enabled}><durable>false</durable>{filter}</queue></anycast></address>';
const CFS_ADDRESS_FILTER_TEMPLATE =
  '((&quot;m.block-{cfs}&quot; IS NULL) OR (&quot;m.target-{cfs}&quot; IS NOT NULL)) AND ((&quot;m.x-opt-authorized&quot; IS NOT NULL))';
const TRUSTED_CFS_ADDRESS_FILTER_TEMPLATE =
  "((&quot;m.block-{trusted-cfs}&quot; IS NULL) OR (&quot;m.target-{trusted-cfs}&quot; IS NOT NULL)) AND (&quot;m.x-opt-systems&quot; LIKE '%{system}%')";
const SECURITY_TEMPLATE =
  '<security-setting match="iot.{serviceName}.{address}"><permission type="{type}" roles="{diamServiceName}" /></security-setting>';
const HUSQIOT_EXTENSION_ADDRESS_TEMPLATE =
  '<address>iot.{serviceName}.{address}</address>';


function formatXml(xmlData: string, keepComment: boolean = false): string {
  if (keepComment) {
    return xmlFormat(xmlData, {
      collapseContent: true,
      indentation: '  ',
      whiteSpaceAtEndOfSelfclosingTag: true,
    });
  }
  return xmlFormat(xmlData, {
    filter: (node) => node.type !== 'Comment',
    collapseContent: true,
    indentation: '  ',
    whiteSpaceAtEndOfSelfclosingTag: true,
  });
}


export function generateXml(
  services: Service[], templates: Template[], basicData: BasicData, auditConfig: AuditConfig): XmlFilesContent {
  const xmls: {
    addresses: string;
    security: string;
    devDiverts: string | undefined;
    qaDiverts: string | undefined;
    liveDiverts: string | undefined;
    extension: string;
  }[] = services.map((service) =>
    getXmlForService(
      service.name,
      service.diamServiceName,
      templates,
      service,
      basicData,
      auditConfig
    )
  );
  const addressFile = getFile(templates, 'addresses.xml');
  const securityFile = getFile(templates, 'security-settings.xml');
  const divertsDevFile = getFile(templates, 'dev-diverts.xml');
  const divertsQaFile = getFile(templates, 'qa-diverts.xml');
  const divertsLiveFile = getFile(templates, 'live-diverts.xml');
  const extensionFile = getFile(templates, 'husqiot-extension.xml');

  // Join all addresses for all added services in tool and add to exiting
  // services in addresses.xml file
  const addressesForAddedServices: string[] = xmls.map((xml) =>
    xml.addresses
      ?.replace('<addresses xmlns="urn:activemq:core">', '')
      .replace('</addresses>', '')
  );
  addressesForAddedServices.push('</addresses>');
  const serviceAdresses: string = addressesForAddedServices.join('');
  const addresses = addressFile.content.replace(
    '</addresses>',
    serviceAdresses
  );

  // Join all security-settings for all added services in tool and add to exiting
  // services in security-settings.xml file
  const securitySettingsForAddedServices: string[] = xmls.map((xml) =>
    xml.security
      .replace('<security-settings xmlns="urn:activemq:core">', '')
      .replace('</security-settings>', '')
  );
  securitySettingsForAddedServices.push('</security-settings>');
  const serviceSecurity: string = securitySettingsForAddedServices.join('');
  const security = securityFile.content.replace(
    '</security-settings>',
    serviceSecurity
  );

  // Join xml for all diverts and add it to ENV-diverts-file
  const devDivertsForAddedServices: string[] = xmls.map(
    (xml) =>
      xml.devDiverts
        ?.replace('<diverts xmlns="urn:activemq:core">', '')
        .replace('</diverts>', '') || ''
  );
  const qaDivertsForAddedServices: string[] = xmls.map(
    (xml) =>
      xml.qaDiverts
        ?.replace('<diverts xmlns="urn:activemq:core">', '')
        .replace('</diverts>', '') || ''
  );
  const liveDivertsForAddedServices: string[] = xmls.map(
    (xml) =>
      xml.liveDiverts
        ?.replace('<diverts xmlns="urn:activemq:core">', '')
        .replace('</diverts>', '') || ''
  );
  const devDiverts = divertsAsXml(devDivertsForAddedServices, divertsDevFile);
  const qaDiverts = divertsAsXml(qaDivertsForAddedServices, divertsQaFile);
  const liveDiverts = divertsAsXml(liveDivertsForAddedServices, divertsLiveFile
  );

  // add basic data to husqiot-extensions.xml file
  const basicDataXml = generateBasicDataXml(basicData);

  // Join incomings and outgoings for all added services in tool and add to exiting
  // services in husqiot-extensions.xml file
  const incomings: string[] = [];
  const outgoings: string[] = [];
  const transformers: string[] = [];
  const mappings: string[] = [];
  xmls.forEach((xml) => {
    const ext = xml.extension;
    const firstIncoming = ext.indexOf('<incoming>');
    const lastIncoming = ext.lastIndexOf('</incoming>') + 11;
    const firstOutgoing = ext.indexOf('<outgoing>');
    const lastOutgoing = ext.lastIndexOf('</outgoing>') + 11;
    const firstTransformer = ext.lastIndexOf('<transformer>');
    const lastTransformer = ext.lastIndexOf('</transformer>') + 14;
    const firstMapping = ext.indexOf('<mapping');
    const lastMapping = ext.indexOf('</connectivity-api>');
    const incomingsForService =
      firstIncoming === -1 ? '' : ext.substring(firstIncoming, lastIncoming);
    const outgoingForService =
      firstOutgoing === -1 ? '' : ext.substring(firstOutgoing, lastOutgoing);
    const transformersForService =
      firstTransformer === -1
        ? ''
        : ext.substring(firstTransformer, lastTransformer);
    const mappingForService =
      firstMapping === -1 ? '' : ext.substring(firstMapping, lastMapping);
    incomings.push(incomingsForService);
    outgoings.push(outgoingForService);
    transformers.push(transformersForService);
    mappings.push(mappingForService);
  });

  const extensionXml = extensionFile.content
    .replace('{basicData}', basicDataXml)
    .replace(
      '<!-- Add new incomings -->',
      incomings.filter((item) => item !== '').join('\r\n    ')
    )
    .replace('<!-- Add new outgoings -->', outgoings.join('\r\n    '))
    .replace('<!-- Add new transformers -->', transformers.join(''))
    .replace('<!-- Add new mappings -->', cleanUpMappings(mappings).join('  '));
  const extensions = formatXml(extensionXml, true);

  return {
    addresses: addresses,
    security: security,
    devDiverts: devDiverts,
    qaDiverts: qaDiverts,
    liveDiverts: liveDiverts,
    extension: extensions,
  };
}

function cleanUpMappings(mappings: string[]) {
  const mappingsWithoutEmpty = mappings.filter((item) => item !== '');
  if (mappingsWithoutEmpty.length <= 0) {
    return mappingsWithoutEmpty;
  }
  const lastItemWithoutNewLine = mappingsWithoutEmpty[
    mappingsWithoutEmpty.length - 1
  ].replace('\r\n', '');
  mappingsWithoutEmpty[mappingsWithoutEmpty.length - 1] =
    lastItemWithoutNewLine;
  return mappingsWithoutEmpty;
}

function divertsAsXml(
  divertsForAddedServices: string[],
  divertsDevFile: NameContent
) {
  divertsForAddedServices.push('</diverts>');
  const serviceDiverts: string = divertsForAddedServices.join('');
  const diverts = divertsDevFile.content.replace(
    '</diverts>',
    serviceDiverts || '</diverts>'
  );
  return diverts;
}

export function getXmlForService(
  serviceName: string, diamServiceName: string, templates: Template[], selectedService: Service, basicData: BasicData, auditConfig: AuditConfig): XmlFilesContent {
  if (selectedService.xml) {
    return selectedService.xml;
  }
  return generateXmlConfigsForService(
    serviceName,
    diamServiceName,
    templates,
    selectedService,
    basicData,
    auditConfig
  );
}

/**
 * Given templates generates xml-data for all different file types.
 * @param serviceName name of service, needs to be provided seperatly for update in webpage
 * @param diamServiceName this service diam-name, needs to be provided seperatly for update in webpage
 * @param templates templates to generate xml from
 * @param selectedService selected service
 * @returns xml-data for the service
 */
export function generateXmlConfigsForService(
  serviceName: string, diamServiceName: string, templates: Template[], selectedService: Service, basicData: BasicData, auditConfig: AuditConfig): XmlFilesContent {
  const serviceType: string = selectedService.type;

  const selectedAcceptors = selectedService.acceptors
    .filter((selectedAcceptor) => selectedAcceptor.checked)
    .map((selAcc) =>
      basicData.acceptors.find((acceptor) => acceptor.name === selAcc.name)
    );

  const addresses = generateAddresses(
    selectedService.addresses || [],
    serviceType,
    serviceName,
    selectedAcceptors
  );

  const security = generateSecuritySettings(
    selectedService.addresses || [],
    serviceName,
    diamServiceName
  );

  const divertsForEnvironments = generateDiverts(
    serviceType,
    templates,
    serviceName,
    selectedService?.diverts,
    auditConfig
  );

  const extension = generateExtensions(
    selectedService,
    serviceType,
    templates,
    serviceName,
    basicData
  );

  return {
    addresses,
    security,
    devDiverts: divertsForEnvironments.devDiverts,
    qaDiverts: divertsForEnvironments.qaDiverts,
    liveDiverts: divertsForEnvironments.liveDiverts,
    extension,
  };
}

function generateAddresses(
  addressesInput: Address[],
  serviceType: string,
  serviceName: string,
  acceptors: (AcceptorBasicData | undefined)[]
) {
  const filters = getAddressFilter(acceptors, serviceType, serviceName);
  const addresses = addressesInput
    .map((address) => {
      if (address.checked) {
        const addressXml = ADDRESS_TEMPLATE.replaceAll(
          '{serviceName}',
          serviceName
        )
          .replaceAll('{address}', address.name)
          .replaceAll(
            '{enabled}',
            address.name === 'out' ? 'enabled="false"' : ''
          )
          .replaceAll(
            '{filter}',
            filters && address.name === 'unsolicited.in'
              ? `<filter string="${filters}"/>`
              : ''
          );
        return addressXml;
      }
      return '';
    })
    .join('');
  return formatXml(
    '<addresses xmlns="urn:activemq:core">'
      .concat(addresses)
      .concat('</addresses>')
  );
}

function getAddressFilter(
  acceptors: (AcceptorBasicData | undefined)[],
  serviceType: string,
  serviceName: string
) {
  if (serviceType === CFS) {
    return CFS_ADDRESS_FILTER_TEMPLATE.replaceAll('{cfs}', serviceName);
  }
  if (
    serviceType !== TRUSTED_CFS &&
    (!acceptors || acceptors.length === 0 || acceptors.join('').length === 0)
  ) {
    return undefined;
  }
  return acceptors
    .filter((acc) => acc?.system)
    .map((acceptor) =>
      TRUSTED_CFS_ADDRESS_FILTER_TEMPLATE.replace(
        '{system}',
        acceptor?.system || ''
      ).replaceAll('{trusted-cfs}', serviceName)
    )
    .join(' OR ');
}

function generateSecuritySettings(
  addressesInput: Address[],
  serviceName: string,
  diamServiceName: string
) {
  const security = addressesInput
    .map((address) => {
      if (address.checked) {
        const xml = SECURITY_TEMPLATE.replaceAll('{serviceName}', serviceName)
          .replaceAll('{address}', address.name)
          .replaceAll('{type}', address.name === 'out' ? 'send' : 'consume')
          .replaceAll('{diamServiceName}', diamServiceName);
        return xml;
      }
      return '';
    })
    .join('');
  const securityXml = '<security-settings xmlns="urn:activemq:core">'
    .concat(security)
    .concat('</security-settings>');
  return formatXml(securityXml);
}

function getFile(templates: Template[], fileName: string) {
  const file = templates.find((f) => f.name === fileName);
  if (!file) {
    throw new Error(`Missing file ${fileName}`);
  }
  return file;
}

function generateExtensions(
  selectedService: Service,
  serviceType: string,
  templates: Template[],
  serviceName: string,
  basicData: BasicData
) {
  const extensionGenFile = getFile(
    templates,
    `${serviceType}-husqiot-extension-general.xml`
  );

  const incoming = generateIncomingConfig(
    selectedService,
    templates,
    serviceType,
    serviceName
  );
  const outgoing = generateOutgoingConfig(
    selectedService,
    templates,
    serviceType,
    serviceName
  );

  const transformers = generateTransformersConfig(
    selectedService,
    templates,
    serviceType,
    serviceName,
    basicData
  );

  const extension = extensionGenFile.content
    ?.replace('<!-- ADD INCOMING -->', incoming)
    .replace('<!-- ADD OUTGOING -->', outgoing)
    .replace('<!-- ADD TRANSFORMERS -->', transformers);

  return formatXml(extension);
}

function generateIncomingConfig(
  selectedService: Service,
  templates: Template[],
  serviceType: string,
  serviceName: string
) {
  const outAddressExists = selectedService.addresses.find(
    (address) => address.name === 'out'
  )?.checked;
  if (!outAddressExists) {
    return '';
  }

  const additionalAnnotators = checkboxesToXml(
    selectedService?.annotators,
    'annotate annotator'
  );
  const additionalAnnotatorsString =
    additionalAnnotators.join('').length > 0
      ? `<annotations>${additionalAnnotators.join('')}</annotations>`
      : '';
  const additionalAcceptors = checkboxesToXml(
    selectedService?.acceptors,
    'accept acceptor'
  );
  const additionalAuthorizers = checkboxesToXml(
    selectedService?.authorizers,
    'authorize authorizer'
  );
  const additionalValidators = checkboxesToXml(
    selectedService?.validators,
    'validate validator'
  );
  const additionalDivert = selectedService.diverts
    .filter((divert) => divert.type === requestResponse)
    .map((divert) => `<service>${divert.serviceName}</service>\n            `);

  const incomingFile = getFile(templates, `${serviceType}-incoming.xml`);

  const incoming = incomingFile.content
    ?.replaceAll(`{${serviceType}}`, serviceName)
    .replaceAll(ANNOTATIONS_TEMPLATE, additionalAnnotatorsString)
    .replaceAll(
      '</accepts>',
      [additionalAcceptors?.join(''), '</accepts>'].join('')
    )
    .replaceAll(
      '<authorize authorizer={token-authorizer} />',
      additionalAuthorizers?.join('')
    )
    .replaceAll(
      '</validations>',
      [additionalValidators?.join(''), '</validations>'].join('')
    )
    .replaceAll(
      '</remote-services>',
      additionalDivert.length > 0
        ? [additionalDivert?.join(''), '</remote-services>'].join('')
        : ''
    )
    .replaceAll(
      '<remote-services>',
      additionalDivert.length > 0 ? '<remote-services>' : ''
    )
    .replace(/<requesting-services>\r?\n/g, '')
    .replace(/<\/requesting-services>\r?\n/g, '');

  return incoming;
}

function generateOutgoingConfig(
  selectedService: Service,
  templates: Template[],
  serviceType: string,
  serviceName: string
) {
  const additionalAcceptors = checkboxesToXml(
    selectedService?.acceptors,
    'accept acceptor'
  );

  if (
    serviceType === ADAPTER ||
    serviceType === BIGDATA_INTEGRATION ||
    serviceType === NON_CFS
  ) {
    const addressXmls = selectedService.addresses
      .map((address) => {
        if (address.checked && address.name !== 'out') {
          const addressXml = HUSQIOT_EXTENSION_ADDRESS_TEMPLATE.replaceAll(
            '{serviceName}',
            serviceName
          ).replaceAll('{address}', address.name);
          return addressXml;
        }
        return '';
      })
      .join('');
    if (addressXmls === '') {
      return '';
    }
    const outgoingFile = getFile(templates, `${serviceType}-outgoing.xml`);
    const outgoing = outgoingFile.content
      ?.replaceAll(`{${serviceType}}`, serviceName)
      .replaceAll('<!-- ADD ADDRESSES -->', addressXmls)
      .replaceAll(
        '</accepts>',
        [additionalAcceptors?.join(''), '</accepts>'].join('')
      );
    return outgoing;
  }

  const responseInChecked = selectedService.addresses.find(
    (address) => address.name === 'response.in'
  )?.checked;
  const unsolicitedInChecked = selectedService.addresses.find(
    (address) => address.name === 'unsolicited.in'
  )?.checked;
  if (!responseInChecked && !unsolicitedInChecked) {
    return '';
  }
  const outgoings: string[] = [];
  if (responseInChecked) {
    const outgoingFile = getFile(
      templates,
      `${serviceType}-outgoing-response.xml`
    );
    outgoings.push(
      outgoingFile.content
        ?.replaceAll(`{${serviceType}}`, serviceName)
        .replaceAll(
          '</accepts>',
          [additionalAcceptors?.join(''), '</accepts>'].join('')
        )
    );
  }
  if (unsolicitedInChecked) {
    const outgoingFile = getFile(
      templates,
      `${serviceType}-outgoing-unsolicited.xml`
    );
    outgoings.push(
      outgoingFile.content
        ?.replaceAll(`{${serviceType}}`, serviceName)
        .replaceAll(
          '</accepts>',
          [additionalAcceptors?.join(''), '</accepts>'].join('')
        )
    );
  }
  return outgoings.join('');
}

function generateTransformersConfig(
  selectedService: Service,
  templates: Template[],
  serviceType: string,
  serviceName: string,
  basicData: BasicData
) {
  const unsolicitedAddressExists = selectedService.addresses.find(
    (address) => address.name === 'unsolicited.in'
  )?.checked;
  if (!unsolicitedAddressExists) {
    return '';
  }
  const systemsXml: string[] = getXmlForSystems(selectedService, basicData);
  const updatedExtensionFile = generateAuthorizerXml(
    basicData,
    selectedService
  );
  const transformFile = getFile(templates, `${serviceType}-transformers.xml`);
  const outgoing = transformFile.content
    ?.replaceAll(`{${serviceType}}`, serviceName)
    .replace(SYSTEM_XML_TEMPLATE, systemsXml.join('')) // transformer
    .replace('<!-- ADD AUTHORIZATIONS -->', updatedExtensionFile); // transformer;
  return outgoing;
}

function generateAuthorizerXml(
  basicData: BasicData,
  selectedService: Service
): string {
  if (selectedService.type !== 'cfs') {
    return '';
  }

  const selectedAuthorizers = selectedService.authorizers
    .filter((selectedAuth) => selectedAuth.checked)
    .map((selAuth) =>
      basicData.authorizers.find((auth) => auth.name === selAuth.name)
    );

  const authXml = selectedAuthorizers
    .map((auth) => {
      const template = auth?.role
        ? CFS_AUTHORIZER_ROLE_TEMPLATE
        : CFS_AUTHORIZER_TEMPLATE;
      const systemsXmls: string[] = auth?.systems?.map((system) =>
        SYSTEM_XML_TEMPLATE.replace('{systemUsers}', system)
      ) || [''];
      return systemsXmls
        .map((systemXml) =>
          template
            .replace('{cfs}', selectedService.name)
            .replace('{system}', systemXml)
        )
        .join('');
    })
    .join('');
  if (authXml !== '') {
    return `<authorizations>${authXml}</authorizations>`;
  }
  return '';
}

function getXmlForSystems(
  selectedService: Service,
  basicData: BasicData
): string[] {
  return selectedService?.authorizers.map((authorizer) => {
    if (authorizer.checked) {
      const user = getSystemString(authorizer.name, basicData);
      if (user) {
        const userIdXml =
          '                          ' +
          SYSTEM_XML_TEMPLATE.replace('{systemUsers}', user) +
          '\n';
        return userIdXml;
      }
    }
    return '';
  });
}

function getSystemString(
  authorizer: string,
  basicData: BasicData
): string | undefined {
  const selectedUserId = basicData.systems
    .filter((system) => system.authorizer === authorizer)
    .map((system) => system.name);
  if (selectedUserId.length > 1) {
    throw new Error(`Several systems found for authorizer ${authorizer}`);
  }
  return selectedUserId ? selectedUserId[0] : undefined;
}

function checkboxesToXml(
  checkboxTypes: CheckboxConfigType[],
  typeString: string
): string[] {
  return checkboxTypes.map((checkBoxType) => {
    if (checkBoxType.checked && typeString === 'annotate annotator') {
      return `<${typeString}="${checkBoxType.name}"/>`;
    }
    if (checkBoxType.checked && !checkBoxType.disabled) {
      return `<${typeString}="${checkBoxType.name}"/>`;
    }
    return '';
  });
}

function generateDiverts(
  serviceType: string, templates: Template[], serviceName: string, diverts: Divert[], auditConfig: AuditConfig) {
  const sortedDiverts = sortDiverts(diverts);

  const devDiverts = generateDivertsForEnv(
    serviceType,
    templates,
    serviceName,
    sortedDiverts,
    'dev',
    auditConfig
  );
  const qaDiverts = generateDivertsForEnv(
    serviceType,
    templates,
    serviceName,
    sortedDiverts,
    'qa',
    auditConfig
  );
  const liveDiverts = generateDivertsForEnv(
    serviceType,
    templates,
    serviceName,
    sortedDiverts,
    'live',
    auditConfig
  );
  return {
    devDiverts,
    qaDiverts,
    liveDiverts,
  };
}

function getFilter(divert: Divert, serviceType: string) {
  if (
    serviceType === CFS ||
    serviceType === TRUSTED_CFS ||
    serviceType === NON_CFS
  ) {
    return divert.filter ? divert.filter : '';
  }
  return divert.filter ? ` AND ${divert.filter}` : '';
}

function getDivertsToAudit(divertsXml: string): DivertAuditConfig[] {
  const auditConfigs: DivertAuditConfig[] = [];

  const xmlDoc = parser.parseFromString(divertsXml, 'text/xml');
  const diverts = xmlDoc.querySelectorAll('divert');

  for (const divert of diverts) {
    const addressElement = divert.querySelector('address');
    const filterElement = divert.querySelector('filter');
    const filterValue = filterElement ? filterElement.getAttribute('string') || '' : '';
    const divertName = divert.getAttribute('name') || '';

    auditConfigs.push({
      name: divertName,
      address: addressElement?.textContent || '',
      filter: filterValue.replace(/"/g, '&quot;')
    });
  }

  return auditConfigs;
}

function generateDivertsForEnv(
  serviceType: string,
  templates: Template[],
  serviceName: string,
  diverts: Divert[],
  env: string,
  auditConfig: AuditConfig
): string | undefined {
  const addedDivertsXml: string[] = [];
  diverts
    .filter((divert) => divert.environments.includes(env))
    .map((divert) => {
      const divertsFileName = `${serviceType}-${divert.type}-diverts.xml`;

      const divertsFile = getFile(templates, divertsFileName);
      const versionTag = divert.version ? `-${divert.version}` : '';
      const filterTag = getFilter(divert, serviceType);
      let xml =
        divertsFile.content
          ?.replaceAll(`{${serviceType}}`, serviceName)
          .replaceAll('{other}', divert.serviceName)
          .replaceAll('{version}', versionTag)
          .replace('{filter}', filterTag);

      //  All request-response from iot.connectivity-api.out, should be non-exclusive 
      if (
        xml &&
        divert.serviceName === 'connectivity-api' &&
        divert.type === 'request-response'
      ) {
        xml = setExclusiveFalseForAddress(xml, 'iot.connectivity-api.out');
      }


      // All request-response from adapters shall be audited.
      // Do not include diverts for connectivity-api as those are already audited and would cause duplicate messages in audit logs.
      if (serviceType === ADAPTER && divert.type === 'request-response' && auditConfig.environments.includes(env) &&
        !auditConfig.excludedAdapters.includes(serviceName) && divert.serviceName !== 'connectivity-api') {
        const auditDivertsFile = getFile(templates, `audit-request-response-diverts.xml`);
        const auditConfigs = getDivertsToAudit(xml);

        const auditXmlParts = auditConfigs.map((auditConfig) => {
          return auditDivertsFile.content
            ?.replaceAll('{divertName}', auditConfig.name)
            .replaceAll('{outAddress}', auditConfig.address)
            .replace('{requestFilter}', auditConfig.filter)
            .replace('<diverts xmlns="urn:activemq:core">', '')
            .replace('</diverts>', '') || '';
        });
        addedDivertsXml.push(...auditXmlParts);
      }

      addedDivertsXml.push(xml
        .replace('<diverts xmlns="urn:activemq:core">', '')
        .replace('</diverts>', '') || '');
    });

  if (
    diverts.find(
      (divert) =>
        divert.type === 'request-response' && divert.environments.includes(env)
    )
  ) {
    const divertsFileName = `${serviceType}-rejected-divert.xml`;
    const divertsFile = getFile(templates, divertsFileName);
    const rejectedDivertXml = divertsFile.content?.replaceAll(
      `{${serviceType}}`,
      serviceName
    );
    addedDivertsXml.push(rejectedDivertXml || '');
  }

  const addedDivertsXmlFormatted = formatXml(
    `<diverts>${addedDivertsXml.join('')}</diverts>`
  )
    .replace('connectivity-api.request.in', 'connectivity-api.in')
    .replace('connectivity-api.response.in', 'connectivity-api.in')
    .replace('<diverts>\r\n', '')
    .replace('</diverts>\r\n', '')
    .replace('<diverts>', '')
    .replace('</diverts>', '');
  return addedDivertsXmlFormatted;
}
